import React from "react";
import {Alert, Button, Form, Modal, Spinner} from "react-bootstrap";
import {useFormik} from "formik";
import * as Yup from "yup";

import {SaveLeasePackageCatalogDto} from "../../model/LeasePackageCatalog";

interface CreateCatalogModalProps {
    show: boolean;
    loading: boolean;
    error?: Error;
    onHide: () => void;
    submitCallback: (dto: SaveLeasePackageCatalogDto) => Promise<void>;
}

const schema = Yup.object().shape({
    name: Yup.string().trim().required("Name is required").max(255, "Name must not exceed 255 characters"),
});

export default function CreateCatalogModal({
    show,
    loading,
    error,
    onHide,
    submitCallback,
}: CreateCatalogModalProps) {
    const formik = useFormik<SaveLeasePackageCatalogDto>({
        initialValues: {name: ""},
        validationSchema: schema,
        onSubmit: async values => {
            try {
                await submitCallback({name: values.name.trim()});
            } catch (e) {
                // The parent exposes the request error in the modal.
            }
        },
    });
    const pending = loading || formik.isSubmitting;

    function hide() {
        if(!pending) onHide();
    }

    return (
        <Modal
            show={show}
            onHide={hide}
            backdrop={pending ? "static" : true}
            keyboard={!pending}
        >
            <Modal.Header closeButton={!pending}>
                <Modal.Title>Create Catalog</Modal.Title>
            </Modal.Header>
            <Form onSubmit={formik.handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert variant="danger" role="alert">
                            The create response is unclear. A catalog refresh was attempted; close this dialog and check the list before retrying.
                        </Alert>
                    )}
                    <Form.Group controlId="create-catalog-name">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            autoFocus
                            type="text"
                            name="name"
                            value={formik.values.name}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            disabled={pending}
                            isInvalid={formik.touched.name && !!formik.errors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.name}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={hide} disabled={pending}>
                        Close
                    </Button>
                    <Button variant="primary" type="submit" disabled={pending}>
                        {pending && <Spinner animation="border" size="sm" className="me-2" />}
                        Create
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
